import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { majorToMinor } from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateSubscriptionPlan } from "@/features/fee-engine/api/use-create-subscription-plan";
import type { SubscriptionPlanDto } from "@/features/fee-engine/api/dto";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";
import {
  createSubscriptionPlanSchema,
  type SubscriptionPlanFormValues,
} from "@/features/fee-engine/schemas/subscription-plan.schema";

const EMPTY_VALUES: SubscriptionPlanFormValues = {
  code: "",
  name: "",
  minEmployees: 1,
  maxEmployees: 50,
  monthlyPrice: 0,
  currency: "GNF",
};

const toPayload = (
  values: SubscriptionPlanFormValues
): SubscriptionPlanDto => ({
  code: values.code.trim().toUpperCase(),
  name: values.name.trim(),
  minEmployees: values.minEmployees,
  maxEmployees: values.maxEmployees,
  monthlyPriceMinor: majorToMinor(values.monthlyPrice, values.currency),
  currency: values.currency,
});

/**
 * Formulaire de création d'un plan d'abonnement entreprise
 * (`POST /SubscriptionPlans/add`, guide §6.5). Preview avant submit.
 */
export function SubscriptionPlanFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useFeeEngineTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createSubscriptionPlanSchema(), [i18n.language]);

  const create = useCreateSubscriptionPlan();
  const [preview, setPreview] = useState<SubscriptionPlanDto | null>(null);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("fees.plans.create")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="code">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="code">{t("fees.plans.form.code")}</Label>
                <Input
                  id="code"
                  className="uppercase notranslate"
                  placeholder={t("fees.plans.form.codePlaceholder")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) =>
                    field.handleChange(e.target.value.toUpperCase())
                  }
                />
              </div>
            )}
          </form.Field>

          <form.Field name="name">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="name">{t("fees.plans.form.name")}</Label>
                <Input
                  id="name"
                  placeholder={t("fees.plans.form.namePlaceholder")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="minEmployees">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="minEmployees">
                  {t("fees.plans.form.minEmployees")}
                </Label>
                <Input
                  id="minEmployees"
                  type="number"
                  min={1}
                  className="notranslate"
                  placeholder={t("fees.plans.form.minEmployeesPlaceholder")}
                  value={field.state.value ?? ""}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="maxEmployees">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="maxEmployees">
                  {t("fees.plans.form.maxEmployees")}
                </Label>
                <Input
                  id="maxEmployees"
                  type="number"
                  min={1}
                  className="notranslate"
                  placeholder={t("fees.plans.form.maxEmployeesPlaceholder")}
                  value={field.state.value ?? ""}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="monthlyPrice">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="monthlyPrice">
                  {t("fees.plans.form.monthlyPrice")}
                </Label>
                <Input
                  id="monthlyPrice"
                  type="number"
                  min={0}
                  className="notranslate"
                  placeholder={t("fees.plans.form.monthlyPricePlaceholder")}
                  value={field.state.value ?? ""}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="currency">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="currency">
                  {t("fees.plans.form.currency")}
                </Label>
                <Input
                  id="currency"
                  className="uppercase notranslate"
                  maxLength={3}
                  placeholder={t("fees.plans.form.currencyPlaceholder")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) =>
                    field.handleChange(e.target.value.toUpperCase())
                  }
                />
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={t}
      pending={create.isPending}
      onConfirm={() => {
        if (!preview) return;
        create.mutate(preview, {
          onSuccess: () => {
            notifSilence(t("fees.plans.feedback.created"));
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        });
      }}
    />
    </>
  );
}
